import Cookies from 'js-cookie';
import _, { filter } from 'lodash';
import { observe } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';

import moment from 'moment';

import {
  Layout,
  Spin,
  Row,
  Col,
  Tag,
  Button,
  Divider,
  Form,
  Select,
} from 'antd';

import LoadingScreen from 'components/UI/LoadingScreen';
import StyledContent from 'components/UI/StyledContent';
import StyledFooter from 'components/UI/StyledFooter';
import StyledLayout from 'components/UI/StyledLayout';
import 'helpers/HelperFunctions';

import {
  AppStore,
  CarStore,
  CurrentUserStore,
  GeotabConnector,
  IntegrationStore,
  ReportsDataStore,
  ShopStore,
} from 'stores';
import {
  IntegrationCacheStore,
  IssuesTableCacheStore,
  PitstopTableCacheStore,
  ReportCacheStore,
} from 'stores/CacheStore';

import { dealershipRoutes, technicianRoutes, userRoutes } from 'Routes';
import { customFleets } from 'shared';

import { LoginGeotab, Logout } from 'containers/Auth';
import { Header, Sidebar } from 'containers/Shell';
import { LoginPage, RegisterPage, ResetPasswordPage } from 'v3/pages';

import { Loading, SampleDataBar } from 'components';
import OtherFormModal from 'containers/Integration/OtherFormModal';
import OktaCallback from 'v3/components/forms/auth/OktaCallback';
import MicrosoftCallback from 'v3/components/forms/auth/MicrosoftCallback';

import {
  GeotabIntegrationForm,
  ProfileForm,
  SamsaraIntegrationForm,
} from 'v3/components/forms/onboarding';

import { Logger } from 'stores/Classes';
import DeviceStore from 'stores/Classes/DeviceStore';
import { EventBus } from 'stores/EventBus';
import NotificationsContainer from './components/Notifications/NotificationsContainer';
import {
  CloseOutlined,
  ArrowLeftOutlined,
  FilterFilled,
} from '@ant-design/icons';
import ExternalIcon from 'assets/icons/external.svg?react';
import { WorkOrderDetails } from 'containers/WorkOrder';
import DVIRDetailsPage from 'containers/DVIR/DVIRDetailsPage';
import authTokenManager from 'services/AuthTokenManager';
import ResizableDrawer from 'components/ResizableDrawer';
import versionChecker from 'services/versionChecker';

declare global {
  interface Window {
    google: {
      translate: {
        TranslateElement: any;
      };
    };
    googleTranslateElementInit: () => void;
    pendo: {
      initialize: (options: any) => void;
    };
  }
}

const DashboardWrapper = (props: RouteComponentProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [loadingText1, setLoadingText1] = useState(true);
  const [slideOutVisible, setSlideOutVisible] = useState(false);
  const [slideOutContent, setSlideOutContent] = useState<{
    type: string;
    data: any;
    title: string;
    selectedCarId?: number;
    isNew?: boolean;
    openedFromSFTP?: boolean;
  } | null>(null);
  const [slideOutHistory, setSlideOutHistory] = useState<
    Array<{
      type: string;
      data: any;
      title: string;
      selectedCarId?: number;
      isNew?: boolean;
      openedFromSFTP?: boolean;
    }>
  >([]);

  const [isMaximized, setIsMaximized] = useState(false);
  const [filterList, setFilterList] = useState<any>({
    status: [],
    pm: [],
    safety: [],
    dvir: [],
    light: [],
    issues: [],
    location: [],
    tagLocation: [],
  });

  const workOrderDetailsRef = useRef<any>(null);

  const timerRef: React.RefObject<ReturnType<typeof setInterval> | null> =
    useRef(null);
  const isMountedRef = useRef(true);

  const slideOutRef = useRef<{
    visible: boolean;
    content: {
      type: string;
      data: any;
      title: string;
      selectedCarId?: number;
      isNew?: boolean;
      openedFromSFTP?: boolean;
    } | null;
  }>({ visible: false, content: null });

  const reset = () => {
    PitstopTableCacheStore.reset();
    IssuesTableCacheStore.reset();
    IntegrationCacheStore.reset();
    ReportCacheStore.reset();
    CarStore.reset();
    CarStore.demoCarsTable.reset();
  };

  const loadDemos = async () => {
    await CarStore.assignDemoCar(ShopStore.currentShop.id);
    reset();
    loadDemoCars();
    ReportsDataStore.reload();
  };

  const loadDemoCars = async () => {
    try {
      if (ShopStore.currentShop.id !== -1) {
        IntegrationStore.modalIntegrationForm = null;

        const hasCars = await CarStore.checkIfShopHasCars(
          ShopStore.currentShop.id
        );

        if (hasCars) {
          IntegrationStore.loaded = false;
          const hasIntegration =
            await IntegrationStore.checkIfShopHasIntegration(
              ShopStore.currentShop.id
            );
          IntegrationStore.loaded = true;
          const temporaryData = { id: 1, vin: 'DEMO-' };
          if (!hasIntegration) {
            // shop has demo cars linked to it
            // if the shop doesn't have integration, set the CarStore information to tell CarStore to use demo data
            CarStore.demoCarsTable.demo = true;
            CarStore.demoCarsTable.loaded = true;
            CarStore.setCarData(temporaryData, 1);
            CarStore.demoCarsTable.data = [1];

            // if the shop has real cars, unassign demo cars and reset / reload
            if (
              (await CarStore.countShopRealCars(ShopStore.currentShop.id)) > 0
            ) {
              await CarStore.unassignDemoCar();
              reset();
              ReportsDataStore.reload();
            }
          } else {
            // if the shop has integration, set the CarStore information to tell CarStore that the shop has real data
            temporaryData.vin = 'integration_set';
            CarStore.demoCarsTable.loaded = true;
            CarStore.demoCarsTable.demo = false;
            CarStore.setCarData(temporaryData, 1);
            CarStore.demoCarsTable.data = [1];
          }
        } else {
          CarStore.demoCarsTable.loaded = true;
        }
      }
    } catch {
      AppStore.addError('Sorry! We are unable to load vehicles data!');
    } finally {
      IntegrationStore.loaded = true;
    }
  };

  const handleMotiveOauthCode = async (code: any, shopId: any) => {
    try {
      await IntegrationStore.handleMotiveOauth(code, shopId);
    } catch {
      // do nothing
    }
  };

  const initLoadingTextInterval = () => {
    timerRef.current = setInterval(() => {
      setLoadingText1((prev) => !prev);
    }, 3000);
  };

  const clearLoadingTextInterval = () => {
    clearInterval(timerRef.current!);
    timerRef.current = null;
  };

  const googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: 'en',
        layout: window.google.translate.TranslateElement.FloatPosition.TOP_LEFT,
      },
      'google_translate_element'
    );
  };

  const toggleSideBar = () => {
    setCollapsed((prev) => {
      const newState = !prev;
      EventBus.$emit('dashboardWrapper/SIDEBAR_TOGGLED', newState);
      return newState;
    });
  };

  const openSlideOut = (
    type: string,
    data: any,
    title: string,
    selectedCarId?: number,
    isNew?: boolean,
    openedFromSFTP?: boolean
  ) => {
    if (slideOutRef.current.content) {
      setSlideOutHistory((prev) => [...prev, slideOutRef.current.content!]);
    }
    setSlideOutContent({
      type,
      data,
      title,
      selectedCarId,
      isNew,
      openedFromSFTP,
    });
    setSlideOutVisible(true);

    // Emit event to highlight the selected car row
    if (selectedCarId) {
      EventBus.$emit('dashboardWrapper/HIGHLIGHT_CAR_ROW', selectedCarId);
    }
  };

  const goBackSlideOut = () => {
    if (slideOutHistory.length > 0) {
      const previousSlideOut = slideOutHistory[slideOutHistory.length - 1];
      setSlideOutHistory((prev) => prev.slice(0, -1));
      setSlideOutContent(previousSlideOut);
    }
  };

  const closeSlideOut = () => {
    // For DVIR and filters, close directly without checking for unsaved changes
    if (
      slideOutContent?.type === 'dvir' ||
      slideOutContent?.type === 'filter'
    ) {
      forceCloseSlideOut();
      return;
    }

    // For other types, emit event to check for unsaved changes before closing
    EventBus.$emit('dashboardWrapper/CHECK_UNSAVED_BEFORE_CLOSE');
  };

  const forceCloseSlideOut = () => {
    setSlideOutVisible(false);

    setSlideOutContent(null);
    setSlideOutHistory([]);
    setIsMaximized(false); // Reset maximized state when closing

    // Clear car row highlight
    if (slideOutContent?.type !== 'filter') {
      EventBus.$emit('dashboardWrapper/CLEAR_CAR_ROW_HIGHLIGHT');
    }

    setTimeout(() => {
      setSlideOutContent(null);
      setSlideOutHistory([]);
      setIsMaximized(false); // Reset maximized state when closing
    }, 75);
  };

  const openFullPage = () => {
    if (slideOutContent) {
      // For SFTP work orders, maximize instead of opening full page
      if (
        slideOutContent.type === 'work-order' &&
        slideOutContent.openedFromSFTP
      ) {
        setIsMaximized(!isMaximized);
        // Directly update localStorage to force ResizableDrawer to use new width
        const storageKey = `slide-out-${slideOutContent.type || 'default'}-width`;
        const newWidth = !isMaximized ? 2000 : 1200; // Toggle between 2000px and 800px
        localStorage.setItem(storageKey, newWidth.toString());

        return;
      }

      if (slideOutContent.type === 'work-order') {
        const unsavedData = workOrderDetailsRef.current?.getUnsavedData();
        if (slideOutContent.isNew || !slideOutContent.data) {
          // props.history.push('/work-order/add');
          props.history.push({
            pathname: '/work-order/add',
            state: {
              data: {
                ...unsavedData,
              },
            },
          });
        } else {
          // props.history.push(`/work-order/${slideOutContent.data.id}/edit`);
          props.history.push({
            pathname: `/work-order/${slideOutContent.data.id}/edit`,
            state: {
              data: {
                ...unsavedData,
              },
            },
          });
        }
        forceCloseSlideOut();
      } else if (slideOutContent.type === 'dvir') {
        const carId =
          slideOutContent.selectedCarId || slideOutContent.data.carId;
        const query = slideOutContent.data.isTrailer ? '?trailer=true' : '';
        props.history.push(`/dvir/${carId}${query}`);
        forceCloseSlideOut();
      }
    }
  };

  const noAuthRoutes = () => {
    return [
      <Route exact path={['/login', '/']} key="/login" component={LoginPage} />,
      <Route
        exact
        path="/login/geotab"
        key="/login/geotab"
        render={() => <LoginGeotab toggleSideBar={toggleSideBar}></LoginGeotab>}
      />,
      <Route
        exact
        path={['/register']}
        key="/register"
        component={RegisterPage}
      />,
      <Route
        exact
        path="/reset-password"
        key="/reset-password"
        component={ResetPasswordPage}
      />,
      <Route exact path="/callback" key="/callback" component={OktaCallback} />,
      <Route
        exact
        path="/callbackEntra"
        key="/callback-entra"
        render={() => <MicrosoftCallback />}
      />,
      <Redirect from="*" to="/login" key="redirect" />,
    ];
  };

  useEffect(() => {
    slideOutRef.current = {
      visible: slideOutVisible,
      content: slideOutContent,
    };
  }, [slideOutVisible, slideOutContent]);

  useEffect(() => {
    let prevPath = props.location.pathname;
    let prevShopId = ShopStore.currentShop.id;

    const handleCloseSlideOut = () => {
      if (slideOutContent?.type === 'filter') {
        EventBus.$emit('dashboardWrapper/FILTER_CHANGE', {
          action: 'clear',
        });
      }
      forceCloseSlideOut();
    };

    const unlisten = props.history.listen(() => {
      if (
        location.pathname !== prevPath &&
        slideOutContent?.type === 'filter'
      ) {
        prevPath = location.pathname;
        if (slideOutVisible) {
          handleCloseSlideOut();
        }
      }
    });

    const disposer = observe(ShopStore.currentShop, 'id', () => {
      if (ShopStore.currentShop.id !== prevShopId) {
        prevShopId = ShopStore.currentShop.id;
        handleCloseSlideOut();
      }
    });

    return () => {
      unlisten();
      disposer();
    };
  }, [props.history, slideOutVisible]);

  useEffect(() => {
    EventBus.$on('dashboardWrapper/UPDATE_FILTERS', (filters: any) => {
      if (filters === 'clearSlideout') {
        setFilterList({
          status: [],
          pm: [],
          safety: [],
          dvir: [],
          light: [],
          issues: [],
          location: [],
          tagLocation: [],
        });
      }
      setSlideOutContent((prev) =>
        prev
          ? { ...prev, data: { ...prev.data, selectedFilters: filters } }
          : null
      );
    });
    return () => {
      EventBus.$off('dashboardWrapper/UPDATE_FILTERS');
    };
  }, []);

  useEffect(() => {
    // load google translate
    const addScript = document.createElement('script');
    addScript.setAttribute(
      'src',
      '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
    );
    document.body.appendChild(addScript);

    window.googleTranslateElementInit = googleTranslateElementInit;

    // register geotab listener if in geotab dashboard
    if (AppStore.isOnGeotabDashboard()) {
      GeotabConnector.registerListener();
    }

    if (
      !['/login', '/register', '/login/geotab', '/reset-password'].includes(
        props.location.pathname
      )
    ) {
      Cookies.set(
        'latestRoute',
        props.location.pathname + props.location.search,
        {
          expires: 1,
        }
      );
    }

    // Initialize google analytics page view tracking
    props.history.listen((location: any) => {
      ReactGA.set({ page: location.pathname });
      ReactGA.pageview(location.pathname);
    });

    // Validate auth state and get current user
    authTokenManager.validateAuthState().then((isValid) => {
      if (isValid) {
        CurrentUserStore.getCurrentUser(false);
      }
    });

    initLoadingTextInterval();

    // Start version checker
    versionChecker.start();

    // Register slide-out listeners
    EventBus.$on('dashboardWrapper/OPEN_SLIDE_OUT', (payload: any) => {
      openSlideOut(
        payload.type,
        payload.data,
        payload.title,
        payload.selectedCarId,
        payload.isNew,
        payload.openedFromSFTP
      );
    });

    EventBus.$on('dashboardWrapper/CLOSE_SLIDE_OUT', () => {
      forceCloseSlideOut();
    });

    EventBus.$on('dashboardWrapper/UPDATE_SLIDE_OUT', (payload: any) => {
      // Update the slide-out content without closing it
      setSlideOutContent({
        type: payload.type,
        data: payload.data,
        title: payload.title,
        selectedCarId: payload.selectedCarId,
        isNew: payload.isNew,
      });
    });

    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearLoadingTextInterval();
      }

      // Stop version checker
      versionChecker.stop();

      // Clean up EventBus listeners
      EventBus.$off('dashboardWrapper/OPEN_SLIDE_OUT');
      EventBus.$off('dashboardWrapper/CLOSE_SLIDE_OUT');
      EventBus.$off('dashboardWrapper/UPDATE_SLIDE_OUT');
    };
  }, []);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) {
      if (CurrentUserStore.pending && !timerRef.current) {
        initLoadingTextInterval();
      } else if (!CurrentUserStore.pending && timerRef.current) {
        clearLoadingTextInterval();
      }
      if (!AppStore.isOnGeotabDashboard()) {
        const latestRoute = Cookies.get('latestRoute');

        if (props.location.pathname + props.location.search !== latestRoute) {
          if (
            ![
              '/login',
              '/register',
              '/login/geotab',
              '/reset-password',
            ].includes(props.location.pathname)
          ) {
            Cookies.set(
              'latestRoute',
              props.location.pathname + props.location.search,
              {
                expires: 1,
              }
            );
          }

          if (CurrentUserStore.user) {
            const _latestRoute = Cookies.get('latestRoute');

            if (
              !_.isNil(_latestRoute) &&
              _latestRoute !== props.location.pathname + props.location.search
            ) {
              props.history.push(_latestRoute);
            }

            if (_.isNil(_latestRoute)) {
              props.history.push('/vehicles');
            }
          }
        }
      }
    } else didMountRef.current = true;
  });

  useEffect(() => {
    const disposer = observe(ShopStore.currentShop, 'id', async () => {
      const realCars = await CarStore.countShopRealCars(
        ShopStore.currentShop.id
      );
      Logger.log('🚙 realCars on current shop:', realCars);

      // Check if Motive OAuth code is present in URL
      if (props && props.location && props.location.pathname !== '/callback') {
        const params = new URLSearchParams(props.location.search);
        const code = params.get('code');
        if (code) {
          params.delete('code');
          props.history.replace({ search: params.toString() });
          handleMotiveOauthCode(code, ShopStore.currentShop.id);
        }
      }

      if (CurrentUserStore?.loaded) {
        window.pendo.initialize({
          visitor: {
            id: String(CurrentUserStore.user.id), // Required if user is logged in
            email: CurrentUserStore.user.email, // Recommended if using Pendo Feedback, or NPS Email
            full_name: CurrentUserStore.user.name, // Recommended if using Pendo Feedback
            is_paid_user: realCars > 0,
          },
          account: {
            id: String(ShopStore.currentShop.id), // Required if using Pendo Feedback
            name: ShopStore.currentShop.name, // Optional
          },
        });
      }

      if (customFleets.gmrv.includes(ShopStore.currentShop.id)) {
        props.history.push('/cars');
      }

      const doNotRedirectRoutes = [
        '/vehicles',
        '/cars',
        '/issues',
        '/maintenance',
        '/highpriorityvehicles',
        '/location',
        '/contacts',
        '/user-management',
        '/work-order',
        '/work-order/add',
        '/warranties',
        '/purchase-orders',
        '/purchase-orders/add',
        '/parts',
        '/parts/add',
        '/car/',
        '/inspection-forms',
        '/sftp-work-orders',
      ];

      if (
        !doNotRedirectRoutes.find(
          (route) => props.location.pathname.indexOf(route) !== -1
        )
      ) {
        // Enabled Default highpriorityvehicles page for: Fox & James and Sub-Fleets
        const isFoxAndJames = customFleets.foxAndJames.includes(
          ShopStore.currentShop.id
        );

        if (isFoxAndJames) {
          return props.history.push('/highpriorityvehicles');
        } else {
          const { device } = DeviceStore;

          if (['mobile', 'tablet'].includes(device)) {
            return props.history.push('/work-order');
          }

          return props.history.push('/vehicles');
        }
      }

      Logger.log('🚀 Checking ShopStore ID:', ShopStore.currentShop.id);
      Logger.log('🚙 hasRealCars:', CarStore.hasRealCars);

      if (ShopStore.currentShop.id !== -1) {
        Logger.log('✅ Shop is valid. Loading demo cars...');
        await loadDemoCars();

        if (
          CurrentUserStore?.user?.loaded &&
          CurrentUserStore.user.role === 'admin'
        ) {
          Logger.log('👤 User Role is Admin. Checking CarStore status...');
          Logger.log('📊 CarStore Loaded:', CarStore?.demoCarsTable?.loaded);
          Logger.log('🚗 hasDemoCars:', CarStore.hasDemoCars);
          Logger.log('🚙 hasRealCars:', CarStore.hasRealCars);

          if (
            CarStore?.demoCarsTable?.loaded &&
            CarStore.hasDemoCars &&
            CarStore.hasRealCars
          ) {
            Logger.log('🔥 Unassigning demo cars...');
            await CarStore.unassignDemoCar();
            reset();
            ReportsDataStore.reload();
          } else if (
            CarStore?.demoCarsTable?.loaded &&
            !CarStore.hasDemoCars &&
            !CarStore.hasRealCars
          ) {
            Logger.log('⚡ No cars found. Loading demo cars...');
            await loadDemos();
          } else {
            Logger.log('ℹ️ No actions taken. Conditions not met.');
          }
        } else {
          Logger.log('🔒 User is NOT an admin. Skipping car checks.');
        }
      } else {
        Logger.log('❌ Invalid Shop ID. Exiting process.');
      }

      Cookies.set('lastSelectedShop', String(ShopStore.currentShop.id), {
        expires: 5,
      });
    });

    const disposer1 = observe(CurrentUserStore, 'loaded', async () => {
      const realCars = await CarStore.countShopRealCars(
        ShopStore.currentShop.id
      );
      Logger.log('🚙 realCars:', realCars);

      if (CurrentUserStore?.loaded) {
        window.pendo.initialize({
          visitor: {
            id: String(CurrentUserStore.user.id),
            email: CurrentUserStore.user.email,
            full_name: CurrentUserStore.user.name,
            is_paid_user: realCars > 0,
          },
          account: {
            id: String(ShopStore.currentShop.id),
            name: ShopStore.currentShop.name,
          },
        });

        if (realCars <= 0) {
          await loadDemoCars();
        }
      }
    });

    const disposer2 = observe(
      IntegrationStore,
      'modalIntegrationForm',
      async () => {
        if (IntegrationStore.modalIntegrationForm) {
          AppStore.openModals.set(IntegrationStore.modalIntegrationForm, true);
          await loadDemos();
        }
      }
    );

    return () => {
      disposer();
      disposer1();
      disposer2();
    };
  }, []);

  if (CurrentUserStore.pending) {
    return (
      <>
        <LoadingScreen>
          <div>
            <h1>Welcome to Pitstop Dashboard</h1>
            {loadingText1 ? (
              <p>
                Thank you for waiting while our AI Flywheel is hard at work
                analyzing your data!
              </p>
            ) : (
              <p>
                Did You Know? Pitstop's software analyzes over 30 Billion data
                points!
              </p>
            )}
            <Spin />
          </div>
        </LoadingScreen>
      </>
    );
  }

  if (!CurrentUserStore?.loaded) {
    return (
      <>
        <Switch>{noAuthRoutes()}</Switch>
      </>
    );
  }

  let routes;
  if (CurrentUserStore?.loaded) {
    switch (CurrentUserStore?.user?.role) {
      case 'admin':
        routes = dealershipRoutes();
        break;
      case 'dealership':
        routes = dealershipRoutes();
        break;
      case 'technician':
        routes = technicianRoutes();
        break;
      case 'user':
      default:
        routes = userRoutes();
    }

    // update the google translate cookie with the current user's preferred language
    if (CurrentUserStore?.user?.settings?.preferredLanguage) {
      Cookies.set(
        'googtrans',
        `/en/${CurrentUserStore.user.settings.preferredLanguage}`,
        {
          expires: 365,
        }
      );
    }
  }
  const { device } = DeviceStore;
  const updatedmarginleft = collapsed || ['mobile', 'tablet'].includes(device);

  const renderSlideOutContent = () => {
    if (!slideOutContent) return null;

    switch (slideOutContent.type) {
      case 'work-order': {
        const isNew = slideOutContent.isNew || !slideOutContent.data;

        if (isNew) {
          const mockMatch = {
            params: {},
            isExact: true,
            path: '/work-order/add',
            url: '/work-order/add',
          };

          const mockLocation = {
            pathname: '/work-order/add',
            search: '',
            hash: '',
            state: undefined,
          };

          return (
            <WorkOrderDetails
              onInstanceReady={(instance: any) => {
                workOrderDetailsRef.current = instance;
              }}
              match={mockMatch}
              location={mockLocation}
              history={props.history}
              isSlideOutMode={true}
              initialWorkOrderData={slideOutContent.data}
            />
          );
        } else {
          const mockMatch = {
            params: {
              id: slideOutContent.data.id.toString(),
            },
            isExact: true,
            path: '/work-order/:id/edit',
            url: `/work-order/${slideOutContent.data.id}/edit`,
          };

          const mockLocation = {
            pathname: `/work-order/${slideOutContent.data.id}/edit`,
            search: '',
            hash: '',
            state: undefined,
          };

          return (
            <WorkOrderDetails
              onInstanceReady={(instance: any) => {
                workOrderDetailsRef.current = instance;
              }}
              match={mockMatch}
              location={mockLocation}
              history={props.history}
              isSlideOutMode={true}
              initialWorkOrderData={slideOutContent.data}
            />
          );
        }
      }
      case 'dvir': {
        const mockMatch = {
          params: {
            carId:
              slideOutContent.selectedCarId?.toString() ||
              slideOutContent.data.carId.toString(),
          },
          isExact: true,
          path: '/dvir/:carId',
          url: `/dvir/${slideOutContent.selectedCarId || slideOutContent.data.carId}`,
        };

        const mockLocation = {
          pathname: `/dvir/${slideOutContent.selectedCarId || slideOutContent.data.carId}`,
          search: slideOutContent.data.isTrailer ? '?trailer=true' : '',
          hash: '',
          state: undefined,
        };

        return (
          <DVIRDetailsPage
            match={mockMatch}
            location={mockLocation}
            history={props.history}
            isSlideOutMode={true}
            isTrailer={slideOutContent.data.isTrailer}
          />
        );
      }
      case 'filter': {
        // const updateFilterList = (filterList: any) => {
        //   setSlideOutContent((prev) =>
        //     prev
        //       ? { ...prev, data: { ...prev.data, selectedFilters: filterList } }
        //       : null
        //   );
        // };
        // const activeFilters = slideOutContent.data?.selectedFilters || [];
        const filterOptions = slideOutContent.data?.filterOptions || {};
        const hasTaggedLocations = slideOutContent.data?.hasTags || false;

        return (
          <div
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {/* <div style={{ flexShrink: 0 }}> */}
            {/* <div>
                <Row
                  justify="space-between"
                  align="middle"
                  style={{ width: '100%' }}
                >
                  <Col>
                    <h4>Active Filters</h4>
                  </Col>
                  <Col>
                    <Button
                      type="text"
                      disabled={activeFilters.length === 0}
                      onClick={() => {
                        EventBus.$emit('dashboardWrapper/FILTER_CHANGE', {
                          action: 'clear',
                        });
                      }}
                    >
                      Clear All Filters
                    </Button>
                  </Col>
                </Row>
                <Row
                  justify="start"
                  align="middle"
                  style={{ width: '100%' }}
                >
                  {activeFilters?.map((filter: any, index: number) => {
                    const uniqueKey = `${filter.filterType || 'general'}-${filter.key || filter.value}-${index}`;
                    return (
                      <Tag
                        key={uniqueKey}
                        style={{
                          padding: '2px 15px',
                          marginBottom: '4px',
                          background: '#ffffff',
                          borderRadius: '1rem',
                        }}
                        closable
                        onClose={() =>
                          EventBus.$emit('dashboardWrapper/FILTER_CHANGE', {
                            action: 'close',
                            filters: filter,
                          })
                        }
                      >
                        {filter.label}
                      </Tag>
                    );
                  })}
                </Row>
              </div> */}
            <Divider
              orientation="left"
              style={{ borderColor: '#dcdcdcff', fontSize: '20px' }}
            >
              <FilterFilled /> Filters
            </Divider>
            {/* </div> */}
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '16px' }}>
              <Col span={20} offset={2}>
                {!customFleets.ufpFleet.includes(
                  ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
                ) && (
                  <Form.Item
                    label="Filter by Sub-Location"
                    style={{ fontWeight: 'bold', marginBottom: '12px' }}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="-"
                      value={
                        hasTaggedLocations
                          ? filterList.tagLocation
                          : filterList.location
                      }
                      onChange={(value) => {
                        hasTaggedLocations
                          ? setFilterList({ ...filterList, tagLocation: value })
                          : setFilterList({ ...filterList, location: value });
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        typeof option?.children === 'string' &&
                        (option.children as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {[
                        ...filterOptions.location,
                        ...filterOptions.tagLocation,
                      ].map((option: any) => (
                        <Select.Option
                          key={option.id ?? option.value}
                          value={option.id ?? option.value}
                        >
                          {option.description ?? option.text}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                <Form.Item
                  label="Filter by Status"
                  style={{ fontWeight: 'bold', marginBottom: '12px' }}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="-"
                    value={filterList.status}
                    onChange={(value) => {
                      setFilterList({ ...filterList, status: value });
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      typeof option?.children === 'string' &&
                      (option.children as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {filterOptions.status?.map((option: any) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.text}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Filter by Engine Light"
                  style={{ fontWeight: 'bold', marginBottom: '12px' }}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="-"
                    value={filterList.light}
                    onChange={(value) => {
                      setFilterList({ ...filterList, light: value });
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      typeof option?.children === 'string' &&
                      (option.children as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {filterOptions.light?.map((option: any) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.text}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Filter by Issues"
                  style={{ fontWeight: 'bold', marginBottom: '12px' }}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="-"
                    value={filterList.issues}
                    onChange={(value) => {
                      setFilterList({ ...filterList, issues: value });
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      typeof option?.children === 'string' &&
                      (option.children as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {filterOptions.issues?.map((option: any) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.text}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Filter by DOT Inspections"
                  style={{ fontWeight: 'bold', marginBottom: '12px' }}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="-"
                    value={filterList.safety}
                    onChange={(value) => {
                      setFilterList({ ...filterList, safety: value });
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      typeof option?.children === 'string' &&
                      (option.children as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {filterOptions.safety?.map((option: any) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.text}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Filter by PM Deadline"
                  style={{ fontWeight: 'bold', marginBottom: '12px' }}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="-"
                    value={filterList.pm}
                    onChange={(value) => {
                      setFilterList({ ...filterList, pm: value });
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      typeof option?.children === 'string' &&
                      (option.children as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {filterOptions.pm?.map((option: any) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.text}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Filter by Latest DVIR"
                  style={{ fontWeight: 'bold', marginBottom: '12px' }}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="-"
                    value={filterList.dvir}
                    onChange={(value) => {
                      setFilterList({ ...filterList, dvir: value });
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      typeof option?.children === 'string' &&
                      (option.children as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {filterOptions.dvir?.map((option: any) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.text}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </div>
            <div
              style={{
                flexShrink: 0,
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '10px 24px',
                marginTop: '-10px',
                zIndex: 25,
              }}
            >
              <Divider style={{ borderColor: '#dcdcdcff' }} />
              <Row justify="start" align="middle">
                <Button
                  type="default"
                  style={{ marginBottom: '8px', marginRight: '42px' }}
                  // disabled={Object.values(filterList).every(
                  //   (arr) => Array.isArray(arr) && arr.length === 0
                  // )}
                  onClick={() => {
                    setFilterList({
                      status: [],
                      pm: [],
                      safety: [],
                      dvir: [],
                      light: [],
                      issues: [],
                      location: [],
                      tagLocation: [],
                    });
                    EventBus.$emit('dashboardWrapper/FILTER_CHANGE', {
                      action: 'clear',
                    });
                  }}
                >
                  Reset Filters
                </Button>
                <Button
                  type="primary"
                  style={{ marginBottom: '8px' }}
                  onClick={() => {
                    // console.log('filterlist', filterList);
                    EventBus.$emit('dashboardWrapper/FILTER_CHANGE', {
                      action: 'add',
                      filters: filterList,
                    });
                  }}
                >
                  {`Apply ${Object.values(filterList).reduce((sum: number, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)} Filter(s)`}
                </Button>
              </Row>
            </div>
          </div>
        );
      }
      default:
        return <div>Unknown content type</div>;
    }
  };

  return (
    <>
      <Layout>
        <Logout />
        <Loading />
        <Header {...({ toggleSideBar } as any)} />
        <Sidebar
          {...({
            collapsed,
            toggleSideBar,
          } as any)}
        />
        <StyledLayout
          className="dashboard-content"
          collapsed={collapsed}
          device={device}
        >
          <StyledContent
            {...({ updatedmarginleft: updatedmarginleft.toString() } as any)}
          >
            <div id="pendo-helper">&nbsp;</div>

            {/* ✅ Render SampleDataBar only if there are NO real cars */}
            {!CarStore.hasRealCars && <SampleDataBar />}

            <ProfileForm id="PROFILE_FORM_ID" />
            <GeotabIntegrationForm id="geotab-integration-form" />
            <SamsaraIntegrationForm id="samsara-integration-form" />
            <OtherFormModal
              id="other-integration-form"
              shopId={ShopStore.currentShop.id}
              afterCreate={() => {
                Logger.log('🔄 afterCreate');
              }}
              next={() => {}}
            />

            <Switch>{routes}</Switch>
          </StyledContent>
          <StyledFooter style={{ textAlign: 'center' }}>
            <p>Developed by Pitstop Inc &copy; {moment().format('YYYY')}</p>
          </StyledFooter>
        </StyledLayout>
      </Layout>

      {/* Slide-out Drawer */}
      <ResizableDrawer
        data-testid="new-work-order-slider"
        title={
          <Row justify="space-between" align="middle" style={{ width: '100%' }}>
            <Col>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                {slideOutHistory.length > 0 && (
                  <ArrowLeftOutlined
                    onClick={goBackSlideOut}
                    style={{
                      fontSize: '18px',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '4px',
                    }}
                    title="Go back"
                  />
                )}
                <span
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  {slideOutContent ? slideOutContent?.title || 'Details' : ''}
                </span>
              </div>
            </Col>
            <Col>
              <div style={{ display: 'flex', gap: '16px' }}>
                {slideOutContent?.type !== 'filter' && (
                  <div
                    onClick={openFullPage}
                    data-testid="open-full-page-button"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: 'white',
                    }}
                  >
                    <ExternalIcon
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: 'white !important',
                      }}
                    />
                    <span style={{ fontSize: '12px' }}>
                      {slideOutContent?.type === 'work-order' &&
                      (slideOutContent?.openedFromSFTP || false)
                        ? isMaximized
                          ? 'Restore'
                          : 'Maximize'
                        : 'Open'}
                    </span>
                    <span style={{ fontSize: '12px' }}>
                      {slideOutContent?.type === 'work-order' &&
                      (slideOutContent?.openedFromSFTP || false)
                        ? 'Window'
                        : 'Full Page'}
                    </span>
                  </div>
                )}
                <div
                  onClick={closeSlideOut}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: 'white',
                  }}
                >
                  <CloseOutlined
                    style={{
                      fontSize: '16px',
                      marginBottom: '4px',
                      color: 'white !important',
                    }}
                  />
                  <span style={{ fontSize: '12px' }}>Close</span>
                  <span style={{ fontSize: '12px' }}>Window</span>
                </div>
              </div>
            </Col>
          </Row>
        }
        placement="right"
        onClose={closeSlideOut}
        open={slideOutVisible}
        width={(() => {
          if (isMaximized) {
            return 2000; // Large fixed width for maximized state
          }
          if (['mobile', 'tablet'].includes(device)) {
            if (slideOutContent?.type === 'filter') {
              return '40%';
            }
            return '90%';
          }
          if (slideOutContent?.type === 'dvir') {
            return 1200;
          }
          if (slideOutContent?.type === 'filter') {
            return 400;
          }
          return 800;
        })()}
        closable={false}
        mask={false}
        maskClosable={false}
        isDesktop={!['mobile', 'tablet'].includes(device)}
        minWidth={400}
        maxWidth={isMaximized ? 2000 : 1600}
        storageKey={`slide-out-${slideOutContent?.type || 'default'}-width`}
        styles={{
          header: {
            backgroundColor: '#03103d',
            borderBottom: 'none',
            padding: '16px 24px',
          },
          body: {
            padding: '24px',
          },
        }}
      >
        {renderSlideOutContent()}
      </ResizableDrawer>

      <NotificationsContainer />
    </>
  );
};

export default withRouter(observer(DashboardWrapper));
